import React, { useState, useEffect } from 'react';
import AssinaturaModal from '../components/AssinaturaModal';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  CreditCard, 
  Search, 
  ChevronDown, 
  Filter, 
  RefreshCcw, 
  MoreHorizontal,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { firebaseService } from '../services/firebaseService';
import { Assinatura } from '../types/Assinatura';
import { User } from '../types/User';
import { useAuth } from '../contexts/AuthContext';

const Assinaturas: React.FC = () => {
  const { userProfile } = useAuth();
  const [assinaturas, setAssinaturas] = useState<Assinatura[]>([]);
  const [filteredAssinaturas, setFilteredAssinaturas] = useState<Assinatura[]>([]);
  const [alunos, setAlunos] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('assinaturas_searchTerm') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('assinaturas_statusFilter') || 'all';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('assinaturas_sortBy') || 'nextDueDate';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('assinaturas_sortOrder') || 'asc';
  });
  const [selectedAssinaturaId, setSelectedAssinaturaId] = useState<string | null>(null);
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (userProfile?.empresaCnpj) {
        try {
          const [assinaturasData, alunosData] = await Promise.all([
            firebaseService.getAssinaturas(userProfile.empresaCnpj),
            firebaseService.getAlunos(userProfile.empresaCnpj)
          ]);
          
          setAssinaturas(assinaturasData);
          setFilteredAssinaturas(assinaturasData);
          setAlunos(alunosData);
          setLoading(false);
        } catch (error) {
          console.error('Erro ao buscar dados:', error);
          setLoading(false);
          setAssinaturas([]);
          setFilteredAssinaturas([]);
          setAlunos([]);
        }
      } else {
        setLoading(false);
        setAssinaturas([]);
        setFilteredAssinaturas([]);
        setAlunos([]);
      }
    };

    fetchData();
  }, [userProfile]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showStatusFilter) {
        setShowStatusFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusFilter]);

  useEffect(() => {
    let result = [...assinaturas];
    
    // Aplicar filtro de status
    if (statusFilter !== 'all') {
      result = result.filter(assinatura => assinatura.status === statusFilter);
    }
    
    // Aplicar busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(assinatura => {
        const alunoNome = getAlunoNome(assinatura.alunoId).toLowerCase();
        return (
          assinatura.description.toLowerCase().includes(term) || 
          assinatura.id.toLowerCase().includes(term) ||
          assinatura.asaasSubscriptionId.toLowerCase().includes(term) ||
          alunoNome.includes(term)
        );
      });
    }
    
    // Aplicar ordenação
    result.sort((a, b) => {
      let valueA: any = a[sortBy as keyof Assinatura];
      let valueB: any = b[sortBy as keyof Assinatura];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }
      
      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredAssinaturas(result);
  }, [assinaturas, searchTerm, statusFilter, sortBy, sortOrder]);

  // Salvar filtros no localStorage
  useEffect(() => {
    localStorage.setItem('assinaturas_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('assinaturas_statusFilter', statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem('assinaturas_sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('assinaturas_sortOrder', sortOrder);
  }, [sortOrder]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Ativa</span>;
      case 'INACTIVE':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"><XCircle className="h-3 w-3" /> Inativa</span>;
      case 'OVERDUE':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"><XCircle className="h-3 w-3" /> Vencida</span>;
      case 'PENDING':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3" /> Pendente</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getBillingTypeBadge = (type: string) => {
    switch (type) {
      case 'CREDIT_CARD':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"><CreditCard className="h-3 w-3" /> Cartão</span>;
      case 'BOLETO':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Boleto</span>;
      case 'PIX':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">PIX</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{type}</span>;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // Verificar se é formato ISO ou DD/MM/YYYY
    let date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const [day, month, year] = dateString.split('/');
      date = new Date(`${year}-${month}-${day}`);
    }
    
    return date.toLocaleDateString('pt-BR');
  };

  const getAlunoNome = (alunoId: string) => {
    const aluno = alunos.find(a => a.id === alunoId);
    return aluno ? aluno.nome : 'Aluno não encontrado';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assinaturas</h1>
          <p className="text-gray-600">Gerencie as assinaturas dos alunos</p>
        </div>
        <div className="flex items-center gap-x-4">
          <button 
            className="flex items-center gap-x-2 px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
          >
            <RefreshCcw className="h-4 w-4" />
            Limpar Filtros
          </button>
          <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">Nova Assinatura</button>
        </div>
      </div>

      {/* Resultados e Filtros */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {loading ? 'Carregando...' : `${filteredAssinaturas.length} assinatura${filteredAssinaturas.length !== 1 ? 's' : ''} encontrada${filteredAssinaturas.length !== 1 ? 's' : ''}`}
          {statusFilter !== 'all' && (
            <span className="ml-2 text-primary-600">
              (filtrado por: {statusFilter})
            </span>
          )}
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-primary-500 focus:border-primary-500"
            placeholder="Buscar por aluno, descrição ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <div className="relative">
            <button 
              className={`flex items-center gap-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${
                statusFilter !== 'all' 
                  ? 'border-primary-300 bg-primary-50 text-primary-700' 
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
              onClick={() => setShowStatusFilter(!showStatusFilter)}
            >
              <Filter className="h-4 w-4" />
              Status {statusFilter !== 'all' && `(${statusFilter})`}
              <ChevronDown className="h-4 w-4" />
            </button>
            {showStatusFilter && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('ACTIVE');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Ativas
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('INACTIVE');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Inativas
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('OVERDUE');
                      setShowStatusFilter(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Vencidas
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabela de Assinaturas */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-auto">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('alunoId')}
                >
                  Aluno
                  {sortBy === 'alunoId' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('description')}
                >
                  Descrição
                  {sortBy === 'description' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('value')}
                >
                  Valor
                  {sortBy === 'value' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('status')}
                >
                  Status
                  {sortBy === 'status' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('billingType')}
                >
                  Forma de Pagamento
                  {sortBy === 'billingType' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button 
                  className="flex items-center gap-x-1"
                  onClick={() => handleSort('nextDueDate')}
                >
                  Próximo Vencimento
                  {sortBy === 'nextDueDate' && (
                    <ChevronDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAssinaturas.length > 0 ? (
              filteredAssinaturas.map((assinatura) => (
                <tr key={assinatura.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{getAlunoNome(assinatura.alunoId)}</div>
                    <div className="text-xs text-gray-500">ID: {assinatura.alunoId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{assinatura.description}</div>
                    <div className="text-xs text-gray-500">{assinatura.asaasSubscriptionId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(assinatura.value)}</div>
                    <div className="text-xs text-gray-500">Ciclo: {assinatura.cycle}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(assinatura.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getBillingTypeBadge(assinatura.billingType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(assinatura.nextDueDate)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-x-2">
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        onClick={() => setSelectedAssinaturaId(assinatura.id)}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <Edit className="h-5 w-5" />
                      </button>
                      <div className="relative">
                        <button className="text-gray-400 hover:text-gray-600">
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Nenhuma assinatura encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de Detalhes da Assinatura */}
      {selectedAssinaturaId && (
        <AssinaturaModal
          assinaturaId={selectedAssinaturaId}
          onClose={() => setSelectedAssinaturaId(null)}
        />
      )}

      {/* Paginação */}
      {filteredAssinaturas.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">1</span> a <span className="font-medium">{filteredAssinaturas.length}</span> de <span className="font-medium">{filteredAssinaturas.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
                  Anterior
                </button>
                <button className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary-600 focus:z-20">
                  1
                </button>
                <button className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
                  Próximo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assinaturas;
